// client/src/pages/investments/Crypto.js

import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { FaSync, FaPlus, FaTrash, FaEyeSlash, FaEye, FaInfoCircle, FaLink } from 'react-icons/fa';
import useCrypto from '../../hooks/useCrypto';
import { connectWallet, shortAddress } from '../../utils/wallet';

const eur = (v) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));
const signedEur = (v) => `${Number(v) >= 0 ? '+' : ''}${eur(v)}`;
const shortDate = (d) => (d ? new Date(d).toLocaleString('fr-FR') : 'jamais');

// Les quantités crypto vont de 0,00000001 à des millions : on adapte l'affichage.
const qty = (v) => {
  const n = Number(v || 0);
  const digits = n > 0 && n < 1 ? 8 : 4;
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: digits }).format(n);
};

const EMPTY_FORM = { label: '', address: '', chain: 'ethereum' };

const WalletModal = ({ show, chains, saving, onClose, onSubmit }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState('');

  useEffect(() => {
    if (show) {
      setForm(EMPTY_FORM);
      setConnectError('');
    }
  }, [show]);

  // Lecture seule : on ne récupère que l'adresse publique, rien n'est signé.
  const handleConnect = async () => {
    setConnecting(true);
    setConnectError('');
    try {
      const { address, walletName } = await connectWallet();
      setForm((prev) => ({ ...prev, address, label: prev.label || walletName }));
    } catch (err) {
      setConnectError(err.message);
    } finally {
      setConnecting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({ ...form, label: form.label.trim(), address: form.address.trim() });
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Suivre un wallet</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-grid mb-3">
            <Button variant="outline-primary" onClick={handleConnect} disabled={connecting}>
              <FaLink size={12} className="me-2" />
              {connecting ? 'Connexion…' : 'Connecter MetaMask'}
            </Button>
          </div>
          {connectError && (
            <Alert variant="warning" className="py-2" style={{ fontSize: 13 }}>
              {connectError}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Adresse publique</Form.Label>
            <Form.Control
              required
              value={form.address}
              placeholder="0x…"
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
            <Form.Text className="text-muted">
              Seule l&apos;adresse publique est demandée. Ne saisissez <strong>jamais</strong> une
              clé privée ni une phrase de récupération — ici comme ailleurs.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Nom</Form.Label>
            <Form.Control
              required
              value={form.label}
              placeholder="MetaMask principal"
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Chaîne</Form.Label>
            <Form.Select
              value={form.chain}
              onChange={(e) => setForm({ ...form, chain: e.target.value })}
            >
              {chains.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Une même adresse peut être suivie sur plusieurs chaînes : ajoutez-la une fois par
              chaîne.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving || !form.address.trim() || !form.label.trim()}
          >
            {saving ? 'Enregistrement…' : 'Suivre'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const HoldingsTable = ({ holdings, onToggleIgnored, onSetPru }) => {
  if (holdings.length === 0) {
    return (
      <p className="text-muted mb-0">
        Aucune ligne : lancez une synchronisation pour lire les soldes sur la chaîne.
      </p>
    );
  }
  return (
    <div className="table-responsive">
      <Table hover className="align-middle mb-0">
        <thead>
          <tr className="text-muted" style={{ fontSize: 12 }}>
            <th>Token</th>
            <th className="text-end">Quantité</th>
            <th className="text-end">Prix</th>
            <th className="text-end">Valeur</th>
            <th className="text-end">PRU</th>
            <th className="text-end">Plus-value</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {holdings.map((h) => (
            <tr key={h.id} className={h.ignored ? 'opacity-50' : undefined}>
              <td>
                <div className="fw-semibold">
                  {h.symbol}{' '}
                  {h.isNative && (
                    <Badge bg="light" text="dark">
                      natif
                    </Badge>
                  )}
                </div>
                <div className="text-muted text-truncate" style={{ fontSize: 11, maxWidth: 220 }}>
                  {h.name}
                </div>
              </td>
              <td className="text-end">{qty(h.quantity)}</td>
              <td className="text-end">{h.priceEur == null ? '—' : eur(h.priceEur)}</td>
              <td className="text-end fw-semibold">
                {h.valueEur == null ? (
                  <span className="text-muted" title="Prix inconnu de CoinGecko">
                    —
                  </span>
                ) : (
                  eur(h.valueEur)
                )}
              </td>
              <td className="text-end">
                <Button
                  size="sm"
                  variant="link"
                  className="p-0 text-decoration-none"
                  onClick={() => onSetPru(h)}
                >
                  {h.buyingPriceEur == null ? 'saisir' : eur(h.buyingPriceEur)}
                </Button>
              </td>
              <td className={`text-end ${(h.gain || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                {h.gain == null ? <span className="text-muted">—</span> : signedEur(h.gain)}
              </td>
              <td className="text-end">
                <Button
                  size="sm"
                  variant={h.ignored ? 'outline-secondary' : 'outline-warning'}
                  title={h.ignored ? 'Réafficher' : 'Masquer (token spam)'}
                  onClick={() => onToggleIgnored(h)}
                >
                  {h.ignored ? <FaEye size={11} /> : <FaEyeSlash size={11} />}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

function Crypto() {
  const {
    overview,
    loading,
    error,
    setError,
    fetchOverview,
    addWallet,
    removeWallet,
    syncWallet,
    syncAllWallets,
    editHolding
  } = useCrypto();

  const [showModal, setShowModal] = useState(false);
  const [syncSummaries, setSyncSummaries] = useState(null);

  const wallets = overview ? overview.wallets : [];
  const chains = overview ? overview.chains : [{ key: 'ethereum', label: 'Ethereum' }];

  useEffect(() => {
    fetchOverview().catch(() => {
      // erreur déjà remontée par le hook
    });
  }, [fetchOverview]);

  const refresh = () =>
    fetchOverview().catch(() => {
      // erreur déjà remontée par le hook
    });

  const handleSubmit = async (data) => {
    try {
      const wallet = await addWallet(data);
      setShowModal(false);
      // Première synchro immédiate : sinon le wallet apparaîtrait vide.
      setSyncSummaries([await syncWallet(wallet.id)]);
      await refresh();
    } catch {
      // le message d'erreur est déjà affiché
    }
  };

  const handleSyncAll = async () => {
    setSyncSummaries(null);
    try {
      setSyncSummaries(await syncAllWallets(wallets.map((w) => w.id)));
      await refresh();
    } catch {
      // le message d'erreur est déjà affiché
    }
  };

  const handleDelete = async (wallet) => {
    if (!window.confirm(`Ne plus suivre « ${wallet.label} » ? Son historique sera perdu.`)) return;
    try {
      await removeWallet(wallet.id);
      await refresh();
    } catch {
      // le message d'erreur est déjà affiché
    }
  };

  const handleToggleIgnored = async (holding) => {
    try {
      await editHolding(holding.id, { ignored: !holding.ignored });
      await refresh();
    } catch {
      // le message d'erreur est déjà affiché
    }
  };

  const handleSetPru = async (holding) => {
    const current = holding.buyingPriceEur == null ? '' : String(holding.buyingPriceEur);
    const input = window.prompt(
      `Prix de revient unitaire de ${holding.symbol} en € (laisser vide pour effacer) :`,
      current
    );
    if (input === null) return;
    try {
      await editHolding(holding.id, {
        buyingPriceEur: input.trim() === '' ? null : Number(input.replace(',', '.'))
      });
      await refresh();
    } catch {
      // le message d'erreur est déjà affiché
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Crypto</h2>
        <div className="d-flex gap-2">
          {wallets.length > 0 && (
            <Button variant="outline-primary" disabled={loading.syncing} onClick={handleSyncAll}>
              <FaSync size={12} className="me-2" />
              {loading.syncing ? 'Synchro…' : 'Tout synchroniser'}
            </Button>
          )}
          <Button variant="primary" onClick={() => setShowModal(true)}>
            <FaPlus size={12} className="me-2" />
            Suivre un wallet
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {overview && !overview.configured && (
        <Alert variant="warning">
          <strong>Clé Alchemy manquante.</strong> Ajoutez <code>ALCHEMY_API_KEY</code> dans{' '}
          <code>server/.env</code> puis redémarrez le serveur : sans elle, les soldes ne peuvent pas
          être lus sur la chaîne. Un compte gratuit suffit largement.
        </Alert>
      )}

      {syncSummaries && syncSummaries.length > 0 && (
        <Alert variant="success" dismissible onClose={() => setSyncSummaries(null)}>
          {syncSummaries.map((summary) => (
            <div key={summary.label}>
              <strong>{summary.label}</strong> — {summary.found} ligne(s) lue(s) : {summary.created}{' '}
              ajoutée(s), {summary.updated} mise(s) à jour, {summary.closed} soldée(s). Valeur{' '}
              {eur(summary.totalValueEur)}.
              {summary.unpriced > 0 &&
                ` ${summary.unpriced} token(s) sans prix : inconnus de CoinGecko (souvent des airdrops sans valeur) ou quota atteint — réessayez dans quelques minutes.`}
              {summary.warning && ` ${summary.warning}`}
            </div>
          ))}
        </Alert>
      )}

      <Alert variant="light" className="border d-flex align-items-start gap-2">
        <FaInfoCircle className="text-primary mt-1" />
        <div style={{ fontSize: 13 }}>
          Le suivi est en <strong>lecture seule</strong> : seule votre adresse publique est
          enregistrée, jamais une clé privée ni une phrase de récupération. La blockchain indique ce
          que vous détenez, pas ce que vous avez payé — d&apos;où le prix de revient à saisir à la
          main si vous voulez une plus-value.
        </div>
      </Alert>

      {loading.overview && !overview ? (
        <Spinner animation="border" />
      ) : wallets.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-5 text-center">
            <h5>Aucun wallet suivi</h5>
            <p className="text-muted mb-0">
              Connectez MetaMask ou collez une adresse publique pour commencer.
            </p>
          </Card.Body>
        </Card>
      ) : (
        wallets.map((wallet) => (
          <Card key={wallet.id} className="border-0 shadow-sm mb-4">
            <Card.Body className="p-4">
              <Row className="align-items-center g-3 mb-4">
                <Col md>
                  <div className="d-flex align-items-center gap-2">
                    <h5 className="mb-0">{wallet.label}</h5>
                    <Badge bg="light" text="dark">
                      {wallet.chainLabel}
                    </Badge>
                  </div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    <code>{shortAddress(wallet.address)}</code> · synchro{' '}
                    {shortDate(wallet.lastSyncAt)}
                    {wallet.ignoredCount > 0 && ` · ${wallet.ignoredCount} masqué(s)`}
                  </div>
                </Col>
                <Col md="auto" className="text-md-end">
                  <div className="fw-bold" style={{ fontSize: 22 }}>
                    {eur(wallet.totalValue)}
                  </div>
                  {wallet.gain != null && (
                    <div
                      className={wallet.gain >= 0 ? 'text-success' : 'text-danger'}
                      style={{ fontSize: 13 }}
                    >
                      {signedEur(wallet.gain)}
                    </div>
                  )}
                </Col>
                <Col md="auto" className="d-flex gap-2">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    title="Ne plus suivre"
                    onClick={() => handleDelete(wallet)}
                  >
                    <FaTrash size={12} />
                  </Button>
                </Col>
              </Row>

              {wallet.lastSyncError && (
                <Alert variant="warning" className="py-2" style={{ fontSize: 13 }}>
                  Dernière synchro : {wallet.lastSyncError}
                </Alert>
              )}

              <HoldingsTable
                holdings={wallet.holdings}
                onToggleIgnored={handleToggleIgnored}
                onSetPru={handleSetPru}
              />
            </Card.Body>
          </Card>
        ))
      )}

      <WalletModal
        show={showModal}
        chains={chains}
        saving={loading.saving || loading.syncing}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}

export default Crypto;
